import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/fo/Navbar";

export const SignupForm = ({ invite, refCode = "", defaultName = "", compact = false }) => {
  const { register } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "", stage_name: defaultName, referral_code: refCode });
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (!terms) { setBusy(false); return toast.error("Please accept the Terms and the Privacy Policy"); }
      await register({ ...f, invite_slug: invite || null, referral_code: f.referral_code || null, accept_terms: true });
      nav("/dashboard?welcome=1");
    } catch (er) {
      toast.error(errMsg(er));
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="signup-form">
      <Input data-testid="signup-stage-name" required placeholder="Stage name" value={f.stage_name} onChange={set("stage_name")} className="h-12 border-white/10 bg-[#07090E]" />
      <Input data-testid="signup-email" required type="email" placeholder="Email" value={f.email} onChange={set("email")} className="h-12 border-white/10 bg-[#07090E]" />
      <Input data-testid="signup-password" required type="password" minLength={6} placeholder="Password (min 6 characters)" value={f.password} onChange={set("password")} className="h-12 border-white/10 bg-[#07090E]" />
      <Input data-testid="signup-referral-code" placeholder="Invite code from an artist (optional)" value={f.referral_code} onChange={set("referral_code")} className="h-12 border-white/10 bg-[#07090E] uppercase" />
      <label className="flex items-start gap-3 text-xs text-slate-400">
        <Checkbox data-testid="signup-terms-checkbox" checked={terms} onCheckedChange={(v) => setTerms(!!v)} className="mt-0.5 border-white/30" />
        <span>I accept the <Link to="/terms" target="_blank" className="text-[#00F5D4]">Terms</Link> (incl. the broadcast license for my tracks) and the <Link to="/privacy" target="_blank" className="text-[#00F5D4]">Privacy Policy</Link>.</span>
      </label>
      <button data-testid="signup-submit" disabled={busy} className="btn-hot w-full">{busy ? "Creating…" : "Claim my free spot"}</button>
      {!compact && <p className="text-center text-xs text-slate-500">Already in? <Link to="/login" className="text-[#00F5D4]">Log in</Link></p>}
    </form>
  );
};

const Shell = ({ title, sub, children }) => (
  <div className="grid-bg flex min-h-screen items-center justify-center px-4 pb-32 pt-24">
    <div className="panel console-corners w-full max-w-md p-8">
      <Logo />
      <h1 className="mt-8 font-display text-3xl font-black uppercase">{title}</h1>
      <p className="mb-6 mt-2 text-sm text-slate-400">{sub}</p>
      {children}
    </div>
  </div>
);

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(f.email, f.password);
      nav(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (er) {
      toast.error(errMsg(er));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Welcome back" sub="Log in to see your plays, Flames and reports.">
      <form onSubmit={submit} className="space-y-3" data-testid="login-form">
        <Input data-testid="login-email" type="email" required placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="h-12 border-white/10 bg-[#07090E]" />
        <Input data-testid="login-password" type="password" required placeholder="Password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="h-12 border-white/10 bg-[#07090E]" />
        <button data-testid="login-submit" disabled={busy} className="btn-hot w-full">{busy ? "Logging in…" : "Log in"}</button>
        <p className="text-center text-xs text-slate-500"><Link to="/forgot-password" className="text-[#00F5D4]" data-testid="forgot-password-link">Forgot password?</Link> · New artist? <Link to="/join" className="text-[#00F5D4]">Join free</Link></p>
      </form>
    </Shell>
  );
}

export function Register() {
  const [p] = useSearchParams();
  return (
    <Shell title="Join Fuorionda" sub="2 minutes. Free. Then upload your track and go on air.">
      <SignupForm invite={p.get("invite")} refCode={p.get("ref") || ""} />
    </Shell>
  );
}


export function Forgot() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Forgot password" sub="Enter your email and we'll send you a link to choose a new password.">
      {sent ? <p className="text-sm text-[#00F5D4]" data-testid="forgot-sent">If an account exists for this email, a reset link is on its way. Check your inbox (and spam). The link is valid for 1 hour.</p> : (
        <form onSubmit={submit} className="space-y-3" data-testid="forgot-form">
          <Input data-testid="forgot-email" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 border-white/10 bg-[#07090E]" />
          <button data-testid="forgot-submit" disabled={busy} className="btn-hot w-full">{busy ? "Sending…" : "Send reset link"}</button>
        </form>
      )}
      <p className="mt-4 text-center text-xs text-slate-500"><Link to="/login" className="text-[#00F5D4]">Back to log in</Link></p>
    </Shell>
  );
}

export function Reset() {
  const [p] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error("Passwords don't match");
    setBusy(true);
    try { await api.post("/auth/reset-password", { token: p.get("token") || "", password: pw }); toast.success("Password updated — log in with your new password"); nav("/login"); }
    catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <Shell title="New password" sub="Choose a new password for your account.">
      <form onSubmit={submit} className="space-y-3" data-testid="reset-form">
        <Input data-testid="reset-password" type="password" required minLength={6} placeholder="New password (min 6)" value={pw} onChange={(e) => setPw(e.target.value)} className="h-12 border-white/10 bg-[#07090E]" />
        <Input data-testid="reset-password-confirm" type="password" required minLength={6} placeholder="Repeat password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="h-12 border-white/10 bg-[#07090E]" />
        <button data-testid="reset-submit" disabled={busy} className="btn-hot w-full">{busy ? "Saving…" : "Save new password"}</button>
      </form>
    </Shell>
  );
}
